import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, message, Table, Dropdown, Tooltip, Tabs } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, FilterOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ExportButton from '../../../components/common/ExportButton';
import FilterButton from '../../../components/common/FilterButton';
import ExportPanel from '../../../components/common/ExportPanel';
import type { ColumnsType } from 'antd/es/table';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import DisableIcon from '../../../assets/icons/ic-flag.svg?react';
import AddItemButton from '../../../components/common/AddItemButton';
import InviteUserDrawer from '../../exhibits/userManagement/InviteUserDrawer';
import {UserFormData} from '../../exhibits/userManagement/InviteUserDrawer';
import { RootState, AppDispatch } from '../../../store';
import {
  setUsers,
  setCurrentPage,
} from '../../../store/slices/usersSlice';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { User, Company } from '../../../services/types';
import { disableUser, getAllUsers, inviteUser, reenableUser, updateUser } from '@/services/userService';

import { useTableSort } from '../../../hooks';
import UsersFilterDrawer, { UsersFilterState } from './UsersFilterDrawer';
import SortableColumnHeader from '../../../components/common/SortableColumnHeader';
import AdminChangeLogButton from '../changeLog/AdminChangeLogButton';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;


const HeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButtonWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #E5E7EB;
    border-color: #D1D5DB;
  }

  &:active {
    background: #D1D5DB;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  .anticon {
    font-size: 18px;
    color: #6B7280;
  }
`;

const FilterBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #EF4444;
  border-radius: 50%;
`;

const TableWrapper = styled.div`
  position: relative;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  
  .ant-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
  }
  
  .ant-table {
    font-size: 14px;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 14px;
    padding: 16px 12px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
    vertical-align: middle;
  
    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }

    /* Allow text wrap for specific long headers */
    &.wrap-header {
      white-space: normal;
      line-height: 1.4;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 20px 12px;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;
  
    &:last-child {
      border-right: none;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
  
  /* Remove selection highlight */
  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }
`;

// const PaginationContainer = styled.div`
//   padding: 16px 24px;
//   display: flex;
//   justify-content: flex-start;
//   border-top: 1px solid #E5E7EB;
// `;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyStateMessage = styled.p`
  color: #6B7280;
  font-size: 16px;
  margin-bottom: 24px;
`;

// ==================== CHIP COMPONENTS ====================


const UserChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #DBEAFE;
  color: #2563EB;
  
  &::before {
    content: '●';
    font-size: 10px;
  }
`;



const DateText = styled.span`
  font-size: 14px;
  color: #1F2937;
`;

// ==================== ACTION MENU COMPONENTS ====================

const ActionMenuButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

// ==================== MAIN COMPONENT ====================

// Location state interface for navigation
interface LocationState {
  userName?: string | null;
  email?: string | null;
  userType?: string | null;
  companyName?: string | null;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const { tab } = useParams<{ tab?: string }>();
  
  const state = location.state as { defaultTab?: string } | null;

  const getInitialTab = () => {
    if (tab && ['tab1', 'tab2', 'tab3', 'tab4', 'tab5'].includes(tab)) {
      return tab;
    }
    if (state?.defaultTab) {
      return state.defaultTab;
    }
    return 'tab1';
  };
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [userType, setUserType] = useState("externalAdministrators")

  // Get filter params from navigation state
  const locationState = location.state as LocationState | null;
  const userName = locationState?.userName ?? null;
  const email = locationState?.email ?? null;
  const companyName = locationState?.companyName ?? null;
  //const userType = locationState?.userType ?? null;
  const hasFilter = !!userName || !!email ;

  const hasNavigationParams = !!email || !!userName || !!companyName;

  
  // Cycle Drawer states
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [userDrawerMode, setUserDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);

  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  // Ref to scroll to bottom of page
    const contentWrapperRef = useRef<HTMLDivElement>(null);

  // Get current annual report from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);

  // Get user data from Redux
  const { user } = useSelector((state: RootState) => state.auth);

  // Get gaps data from Redux
  const {
    users,
    //totalCount,
    lastFetchedUserType,
    currentPage,
    //pageSize
  } = useSelector((state: RootState) => state.users);

  // Permission checks
  const isSuperAdmin = user?.userType === 'superAdministrator';
  const isExternalAdmin = user?.userType === 'externalAdministrators';

  // Ref to track last fetched params to prevent duplicate API calls
  const lastFetchKeyRef = useRef<string | null>(null);

  const { sortState, handleSort: baseHandleSort, sortedParam } = useTableSort({ defaultField: 'UserName', defaultDirection: 'ascending' });
  
    // Sort loading state (only shows loading on sort icon, not full screen)
    const [isSortLoading, setIsSortLoading] = useState(false);
    // Ref to track previous sortedParam for detecting sort changes
    const prevSortedParamRef = useRef<string | null>(null);
  
    // Filter drawer state - single source of truth for all filters
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filters, setFilters] = useState<UsersFilterState>({});
    // Filter loading state (shows spinner on filter button when applying)
    const [isFilterLoading, setIsFilterLoading] = useState(false);

     // Sync navigation params to filters and reset navigation state
      // This runs once when navigating with params, builds filters, then clears navigation state
      useEffect(() => {
        if (hasNavigationParams) {
          const newFilters: UsersFilterState = {};
  
          // Map navigation email to drawer filter 
          if (email) {
            newFilters.email = email;
          }
 
          // Map navigation email to drawer filter 
          if (userName) {
            newFilters.userName = userName;
          }

          // Map navigation email to drawer filter 
          if (companyName) {
            newFilters.companyName = companyName;
          }
          
          setFilters(newFilters);
          
          // Reset navigation state immediately after building filters
          navigate(location.pathname, { replace: true, state: null });
        }
      }, [
        email,
        userName,
        companyName,
         navigate, 
         location.pathname
        ]
      );


  // Wrap handleSort to set loading immediately when clicked
  const handleSort = useCallback((field: string) => {
    // Only set loading if we have data (not initial load)
    if (users.length > 0) {
      setIsSortLoading(true);
    }
    baseHandleSort(field);
  }, [baseHandleSort, users.length]);

  // Ref to prevent concurrent fetches (fixes double API call issue)
    const isFetchingRef = useRef(false);

  // Fetch gaps when report changes or filter changes
  useEffect(() => {
    const fetchData = async () => {
     
      // Create a unique key for current fetch params
      const fetchKey = `${userType || ''}-${userName || ''}-${email || ''}`;

      // Ref to prevent concurrent fetches (fixes double API call issue)
      //const isFetchingRef = useRef(false);
      
      // Skip if we already fetched with same params
    //   if (lastFetchKeyRef.current === fetchKey) {
    //     console.warn('⏭️ UsersPage: Skipping duplicate fetch for:', fetchKey);
    //     return;
    //   }

      // Skip fetch if navigation params exist - let navigation sync useEffect handle it first
      if (hasNavigationParams) {
        console.warn('⏳ Waiting for navigation params to sync...');
        return;
      }

      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        console.warn('⏳ Already fetching, skipping...');
        return;
      }

      isFetchingRef.current = true;

      // Mark as fetching
      lastFetchKeyRef.current = fetchKey;
      
      // Create current sort key
      const currentSortKey = sortedParam ? `${sortedParam[0]?.name}-${sortedParam[0]?.direction}` : 'default';
      
      // Check if this is initial load or data change (not sort-only)
      const isInitialOrDataChange = prevSortedParamRef.current === null || users.length === 0;
      
      // Update the ref with current sort key
      prevSortedParamRef.current = currentSortKey;
      
      // Set loading states
      if (isInitialOrDataChange) {
        setIsLoading(true);
      }
      setIsFilterLoading(true);
      
      try {
        console.warn(`🔄 Fetching users${hasFilter ? ` with filter (userName: ${userName}, email: ${email}, userType: ${userType})` : ''}...`);
        const response = await getAllUsers({
          sorted: sortedParam,
          email: filters.email ?? undefined,
          userName: filters.userName ?? undefined,
          companyName: filters.companyName,
          userType: userType ?? "externalAdministrators"
        });

        dispatch(setUsers({
          data: response.data,
          totalCount: response.totalCount,
          lastFetchedUserType: response.lastFetchedUserType
        }));

        console.warn(`✅ Fetched ${response.data.length} users`);
      } catch (error) {
        console.error('Error fetching users:', error);
        message.error('Failed to load users');
        // Reset fetch key on error to allow retry
        lastFetchKeyRef.current = null;
      } finally {
        setIsLoading(false);
        setIsSortLoading(false);
        setIsFilterLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [selectedReport, lastFetchedUserType, users.length, dispatch, sortedParam, filters, sortState, activeTab]);

  // Check if any drawer filters are active
    const hasActiveFilters = useMemo(() => {
      return !!(
        filters.email?.length ||
        filters.userName?.length ||
        filters.companyName?.length
      );
    }, [filters]);

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * 10000;
    const end = start + 10000;
    return users.slice(start, end);
  }, [users, currentPage]);

  //const totalPages = Math.ceil(totalCount / pageSize);

  const tabItems = [
      {
        key: 'tab1',
        label: (
          <TabLabel>
            External Admins
          </TabLabel>
        ),
      },
      {
        key: 'tab2',
        label: (
          <TabLabel>
            Internal Admins
          </TabLabel>
        ),
      },
      {
        key: 'tab3',
        label: (
          <TabLabel>
            Process Owners
          </TabLabel>
        ),
      },
      {
        key: 'tab4',
        label: (
          <TabLabel>
            External Users
          </TabLabel>
        ),
      },
      {
        key: 'tab5',
        label: (
          <TabLabel>
            Disabled Users
          </TabLabel>
        ),
      },
    ];
  // Navigation handlers
//   const handleNavigateToCycle = (cycleId: string) => {
//     navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
//   };
 const handleInviteUser = () => {
    setSelectedUserForEdit(null);
    setUserDrawerMode("add");
    setIsUserDrawerOpen(true);
  };

  // Open description drawer
  const handleOpenUser = (user: User) => {
    setSelectedUserForEdit(user);
    setUserDrawerMode("edit");
    setIsUserDrawerOpen(true);
  };

  const handleDisableUser = async (user: User) => {
    if (userType == "disabled") {
        await reenableUser({id: user.id})
    }
    else {
        await disableUser({id: user.id})
    }

    reloadData();
  };

  // Close description drawer
  const handleCloseUser = () => {
    setIsUserDrawerOpen(false); 
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    let newType = "";
        
        switch(newTab) {
        case 'tab1':
            newType = "externalAdministrators"
            break;
        case 'tab2':
            newType = "internalAdministrators"
            break;
        case 'tab3':
            newType = "processOwners"
            break;
        case 'tab4':
            newType = "externalUsers"
            break;
        case 'tab5':
            newType = "disabled"
            break;
        default:
            newType = "externalAdministrators"
            break
    }
    setUserType(newType);
  };

  const handleUserSubmit = async(formData: UserFormData) => {
    try {
          if (userDrawerMode === 'add') {
            // Create new cycle via API
            console.log('Creating new user:', formData);
            await inviteUser({
              firstName: formData.firstName,
              lastName: formData.lastName,
              phoneNumber: formData.phoneNumber ?? "",
              email: formData.email,
              userType: formData.userType,
              companyId: formData.company?.companyId,
              companyIds: formData.companies?.map((item) => item.companyId)
            });
          } else {
            // Edit existing cycle via API
            if (!selectedUserForEdit?.id) {
              message.error('No user selected for editing');
              throw new Error('No user selected for editing');
            }
            await updateUser({
              firstName: formData.firstName,
              lastName: formData.lastName, 
              phoneNumber: formData.phoneNumber ?? "",
              jobTitle: formData.jobTitle,
              department: formData.department,
              location: formData.location,
              id: formData.id ?? "",
              companyName: formData.companyName ?? formData.company?.companyName,
              isPrimaryCompanyUser: formData.isPrimaryCompanyUser,
              companyIds: formData.companies?.map((item) => item.companyId)
            });

          }
    
          // Refresh cycles list (without showing loading)
          const response = await getAllUsers({
            email: email ?? undefined,
            userName: userName ?? undefined,
            userType: userType ?? "externalUsers"
          });

          dispatch(setUsers({
          data: response.data,
          totalCount: response.totalCount,
          lastFetchedUserType: response.lastFetchedUserType
        }));
    
          if (userDrawerMode === 'add') {
            setTimeout(() => {
              if (contentWrapperRef.current) {
                const scrollHeight = contentWrapperRef.current.scrollHeight;
                window.scrollTo({
                  top: scrollHeight,
                  behavior: 'smooth'
                });
              } else {
                // Fallback: scroll to bottom of window
                window.scrollTo({
                  top: document.documentElement.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }, 500);
          }
          message.success(`User ${userDrawerMode === 'add' ? 'created' : 'updated'} successfully`);
        } catch (error) {
          const defaultErrorMessage = `Failed to ${userDrawerMode === 'add' ? 'create' : 'update'} user`;
          message.error(error instanceof Error ? error.message : defaultErrorMessage);
          throw error;
        }
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const formatLongDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: 'numeric'
    });
  };

  // Define columns
  const baseColumns: ColumnsType<User> = [
    {
      title: () => (
        <SortableColumnHeader
          title="User Name"
          field="UserName"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'userName',
      key: 'status',
      width: 80,
      render: (status: string, record) => (
        <UserChip 
          $status={status} 
          onClick={() => handleOpenUser(record)}
          style={{ cursor: 'pointer' }}
        >
          {status}
        </UserChip>
      ),
    },
    {
      title: () => (
        <SortableColumnHeader
          title="Email"
          field="Email"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'email',
      key: 'email',
      width: 100,
      
    },
    ...(userType == "internalAdministrators" || userType == "processOwners" || userType == "disabled" ? 
        [{
      title: () => (
        <SortableColumnHeader
          title="Company Name"
          field="CompanyName"
          sortState={sortState}
          onSort={handleSort}
        />
      ),
      dataIndex: 'companyName',
      key: 'companyName',
      width: 100,
      
    }] :
    [{
      title: 'Company Names',
      dataIndex: 'companies',
      key: 'companies',
      width: 100,
      render: (companies: Company[]) => (
        companies?.map(c => c.companyName).join(", ")
      ),
    }]
    ),
    
    ...(userType == "disabled" ? 
        [{
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      width: 100,
      
}] :
    []
    ),
     {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 40,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
...(userType == "disabled" ? 
        [{
      title: 'Disabled At',
      dataIndex: 'disabledAt',
      key: 'userType',
      width: 70,
      render: (date: string) => (
        <DateText>{formatLongDate(date)}</DateText>
      ),
}] :
    []
    ),
    {
      title: 'Action',
      key: 'actions',
      width: 15,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'edit',
            label: (
              <MenuItemContent>
                <EditIcon />
                <MenuItemText>Edit</MenuItemText>
              </MenuItemContent>
            ),
            onClick: () => handleOpenUser(record),
            disabled: (() => {
              // Super Admin can edit any status
              if (isSuperAdmin || isExternalAdmin) return false;
              // All other cases: disabled
              return true;
            })(),
          },
          {
            type: 'divider',
          },
          {
            key: userType != 'disabled' ? 'disable' : "re-enable",
            label: (
              <MenuItemContent>
                <DisableIcon />
                <MenuItemText $danger>{userType != "disabled" ? "Disable" : "Re-enable"}</MenuItemText>
              </MenuItemContent>
            ),
            onClick: () => handleDisableUser(record),
            disabled: (() => {
              // Super Admin can edit any status
              if (isSuperAdmin || isExternalAdmin) return false;
              // All other cases: disabled
              return true;
            })(),
          },
        ];

        const handleActionButtonClick = (e: React.MouseEvent) => {
          e.stopPropagation();
        };

        return (
          <div onClick={handleActionButtonClick}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionMenuButton>
                <MoreOutlined />
              </ActionMenuButton>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  // Include sortState and isSortLoading to ensure columns update when sort changes
    const columns = useMemo(() => {
      return baseColumns;
    }, [ baseColumns, sortState, tab]);

  // Handle page change
  // const handlePageChange = (page: number) => {
  //   dispatch(setCurrentPage(page));
  // };


  // Handle Export button click
  const handleOpenExportPanel = () => {
    setIsExportPanelOpen(true);
  };

  const handleCloseExportPanel = () => {
    setIsExportPanelOpen(false);
  };

  // Prepare export data with flattened nested objects
  const exportData = useMemo(() => {
    return users.map(item => ({
      userType: item.userType,
      userName: item.userName,
      email: item.email,
      companyName: item.companyName,
      jobTitle: item.jobTitle,
      department: item.department,
      location: item.location,
      createdAt: item.createdAt,
      disabledAt: item.disabledAt
    }));
  }, [users]);

 
  // Reload data
  const reloadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllUsers({
        email: email ?? undefined,
        userName: userName ?? undefined,
        userType: userType ?? "externalAdministrators"
      });
      dispatch(setUsers({
        data: response.data,
        totalCount: response.totalCount,
        lastFetchedUserType: response.lastFetchedUserType
      }));
    } catch (error) {
      console.error('Error refreshing gaps:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userName, email, userType]);

  // Handle clear filter - reset all filters to null and reload data
  const handleClearFilter = async () => {
    
    
    // Navigate first to clear the state
    navigate('/user-management', { state: { userName: null, email: null}, replace: true });

    // Fetch all users without filters
    setIsLoading(true);
    try {
      const response = await getAllUsers({
        userType: userType ?? "externalUsers"
      });
      dispatch(setUsers({
        data: response.data,
        totalCount: response.totalCount,
        lastFetchedUserType: response.lastFetchedUserType
      }));
      
      // Update fetch key after successful fetch
      lastFetchKeyRef.current = `${userType}---`;
    } catch (error) {
      console.error('Error clearing filter:', error);
      message.error('Failed to clear filter');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>User Management</PageTitle>
        </PageHeader>
        <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading users...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Empty state
  if (!isLoading && users.length === 0 && lastFetchedUserType !== null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>User Management</PageTitle>
          <HeaderButtonGroup>  
            {isSuperAdmin || isExternalAdmin ? <AddItemButton text="Invite User" onClick={handleInviteUser} /> : ""}   
          </HeaderButtonGroup>
        </PageHeader>
        <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
        <TableContainer>
          <EmptyStateContainer>
            {hasActiveFilters ? (
              <>
                <EmptyStateMessage>No results found for the current filters.</EmptyStateMessage>
                <AddItemButton 
                  text="Update Filters" 
                  onClick={() => setFilterDrawerOpen(true)} 
                />
              </>
            ) : (
            <EmptyStateMessage>There are no users to display yet.</EmptyStateMessage>
            )}
          </EmptyStateContainer>
        </TableContainer>
         {/* Keep filter drawer accessible in empty state */}
        <UsersFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={(filters) => {
            setFilters(filters);
            dispatch(setCurrentPage(1));
            setFilterDrawerOpen(false);
          }}
          currentFilters={filters}
          annualReportId={selectedReport?.annualReportId || null}
        />
        {/* Cycle Drawer */}
        <InviteUserDrawer
          open={isUserDrawerOpen}
          mode={userDrawerMode}
          initialValues={selectedUserForEdit || undefined}
          
          onClose={handleCloseUser}
          onSubmit={handleUserSubmit}
        />
      </PageContainer>
    );
  }

  // Initial loading state
  if (users.length === 0 && lastFetchedUserType === null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>User Management</PageTitle>
          <HeaderButtonGroup>  
            {isSuperAdmin || isExternalAdmin ? <AddItemButton text="Invite User" onClick={handleInviteUser} /> : ""}   
          </HeaderButtonGroup>
        </PageHeader>
        <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading users...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>

        {/* Cycle Drawer */}
        <InviteUserDrawer
          open={isUserDrawerOpen}
          mode={userDrawerMode}
          initialValues={selectedUserForEdit || undefined}
          
          onClose={handleCloseUser}
          onSubmit={handleUserSubmit}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>User Management</PageTitle>
        <HeaderButtonGroup>
          {hasFilter && (
            <Tooltip title="Clear filter">
              <FilterButtonWrapper onClick={handleClearFilter}>
                <FilterOutlined />
                <FilterBadge />
              </FilterButtonWrapper>
            </Tooltip>
          )}
          {isSuperAdmin || isExternalAdmin ? <AddItemButton text="Invite User" onClick={handleInviteUser} /> : ""}
          <ExportButton onClick={handleOpenExportPanel} title="Export" />
          {/* Filter drawer toggle button - at the end */}
          <FilterButton
            onClick={() => setFilterDrawerOpen(true)}
            hasActiveFilters={hasActiveFilters}
          />
          {isSuperAdmin && (
                      <AdminChangeLogButton

                      />
                    )}
        </HeaderButtonGroup>
      </PageHeader>
          <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      <TableWrapper>
        {(isSortLoading || isFilterLoading) && (
          <LoadingOverlay>
            <LoadingOutlined style={{ fontSize: 32, color: '#3B82F6' }} spin />
          </LoadingOverlay>
        )}
        <TableContainer>
          <Table
            columns={columns}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            scroll={{ x: 1200 }}
            size="middle"
          />

          {/* <PaginationContainer>
            <Pagination
              current={currentPage}
              total={totalCount}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showTotal={() => `${currentPage} of ${totalPages}`}
            />
          </PaginationContainer> */}
        </TableContainer>
      </TableWrapper>

      {/* Cycle Drawer */}
      <InviteUserDrawer
        open={isUserDrawerOpen}
        mode={userDrawerMode}
        initialValues={selectedUserForEdit || undefined}
        
        onClose={handleCloseUser}
        onSubmit={handleUserSubmit}
      />

    

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'userType', title: 'User Type' },
          { key: 'userName', title: 'User Name' },
          { key: 'email', title: 'Email' },
          { key: 'companyName', title: 'Company Name' },
          { key: 'jobTitle', title: 'Job Title' },
          { key: 'department', title: 'Department' },
          { key: 'createdAt', title: 'Created At' },
          
        ]}
        fileName= "Users_Report"
        title="Users Report"
      />

       {/* Keep filter drawer accessible in empty state */}
              <UsersFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                onApply={(filters) => {
                  setFilters(filters);
                  dispatch(setCurrentPage(1));
                  setFilterDrawerOpen(false);
                }}
                currentFilters={filters}
                annualReportId={selectedReport?.annualReportId || null}
              />
      </PageContainer>
  );
};

export default UsersPage;
